import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, RotateCcw, Trash2 } from "lucide-react";
import { imgToBase64 } from "lib/utils/imgToBase64";
import {
  createPagedPdfReport,
  createPdfReport,
  REPORT_PRINT_PDF_LAYOUT,
} from "components/project/PdfDownloadBtn";
import { buildUd1000PdfCoverPages } from "./report/ud1000PdfCover.js";
import ProjectReportPreviewPage from "./report/ProjectReportPreviewPage.jsx";
import {
  downloadProjectElectricalSafetyExcelReport,
  downloadProjectUd1000ExcelReport,
} from "components/project/ProjectReportUdExcelExport";
import ProjectReportDetailConditions from "./ProjectReportDetailConditions";
import { buildProjectDetailFilterGroups } from "components/project/projectDetailFilterGroups";
import ElectricalSafetyTripleSpotModal from "pages/files/image/report/electricalSafety/components/ElectricalSafetyTripleSpotModal";
import { getProjectElectricalSafetyReportData } from "lib/api/getProjectElectricalSafetyReportData";
import { getProjectUd1000ReportData } from "lib/api/getProjectUd1000ReportData";
import { postProjectElectricalSafetyHmlReport } from "lib/api/postProjectElectricalSafetyHmlReport";
import { postProjectUd1000HmlReport } from "lib/api/postProjectUd1000HmlReport";
import { postUpdateThermalInfo } from "lib/api/postUpdateThermalInfo";
import { useAppStore } from "lib/store/useAppStore";
import { getFileSortPref } from "lib/utils/fileSortpref";
import {
  FILE_DETAIL_FILTER_VALUES,
  countFileDetailFilters,
  normalizeFileDetailFilters,
} from "lib/utils/fileDetailFilters";

const REPORT_CATEGORY_OPTIONS = [
  {
    id: "ud1000_fusion",
    label: "UD1000 융복합 진단 보고서",
  },
  {
    id: "electrical_duty",
    label: "전기 직무 고시",
  },
];

const EXPORT_TYPE_OPTIONS = [
  {
    id: "pdf",
    label: "PDF",
  },
  {
    id: "excel",
    label: "EXCEL",
  },
  {
    id: "hwp",
    label: "한글",
  },
];

const COVER_PAGE_OPTIONS = [
  {
    id: "include",
    label: "Insert cover",
    value: true,
  },
  {
    id: "exclude",
    label: "Skip cover",
    value: false,
  },
];

const EMPTY_ELECTRICAL_CIRCLES = [undefined, undefined, undefined];
const UD1000_REPORT_ALL_FILTER_KEY = "all";
const UD1000_REPORT_ALL_FILTER_VALUE = "all";
const EMPTY_UD1000_REPORT_FILTER_VALUE = {
  [UD1000_REPORT_ALL_FILTER_KEY]: [],
  fileType: [],
  facilityType: [],
  fusionResult: [],
};

function getUd1000ReportAllFilterSelection() {
  return {
    ...EMPTY_UD1000_REPORT_FILTER_VALUE,
    [UD1000_REPORT_ALL_FILTER_KEY]: [UD1000_REPORT_ALL_FILTER_VALUE],
  };
}

function activateOnKeyDown(event, callback) {
  if (event.key !== "Enter" && event.key !== " ") return;
  event.preventDefault();
  callback();
}

function isKoreanLanguage(language) {
  return String(language || "")
    .toLowerCase()
    .startsWith("ko");
}

function getDefaultReportCategory(canRenderElectricalDuty) {
  return canRenderElectricalDuty ? "" : "ud1000_fusion";
}

function hasUd1000ReportAllFilter(selection) {
  return Array.isArray(selection?.[UD1000_REPORT_ALL_FILTER_KEY])
    ? selection[UD1000_REPORT_ALL_FILTER_KEY]
        .map(String)
        .includes(UD1000_REPORT_ALL_FILTER_VALUE)
    : false;
}

function hasEveryUd1000ReportDetailFilter(selection) {
  const detailFilters = normalizeFileDetailFilters(selection);

  return Object.entries(FILE_DETAIL_FILTER_VALUES).every(([filterKey, values]) =>
    values.every((value) =>
      (detailFilters[filterKey] ?? []).map(String).includes(String(value)),
    ),
  );
}

function normalizeUd1000ReportFilterSelection(selection) {
  if (!selection) return null;

  if (hasUd1000ReportAllFilter(selection)) {
    return getUd1000ReportAllFilterSelection();
  }

  const detailFilters = normalizeFileDetailFilters(selection);
  if (countFileDetailFilters(detailFilters) <= 0) return null;
  if (hasEveryUd1000ReportDetailFilter(detailFilters)) {
    return getUd1000ReportAllFilterSelection();
  }

  return {
    ...EMPTY_UD1000_REPORT_FILTER_VALUE,
    ...detailFilters,
  };
}

function countUd1000ReportFilterSelection(selection) {
  const normalizedSelection = normalizeUd1000ReportFilterSelection(selection);
  if (!normalizedSelection) return 0;
  if (hasUd1000ReportAllFilter(normalizedSelection)) return 1;
  return countFileDetailFilters(normalizedSelection);
}

function getUd1000ReportQueryFilters(selection) {
  const normalizedSelection = normalizeUd1000ReportFilterSelection(selection);
  if (!normalizedSelection || hasUd1000ReportAllFilter(normalizedSelection)) {
    return {};
  }

  return normalizeFileDetailFilters(normalizedSelection);
}

function reorderItems(items, fromIndex, toIndex) {
  if (
    !Array.isArray(items) ||
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= items.length ||
    toIndex >= items.length
  ) {
    return items;
  }

  const nextItems = [...items];
  const [movedItem] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, movedItem);
  return nextItems;
}

function scrollNodeIntoView(node) {
  if (!node) return;

  node.scrollIntoView({
    behavior: "smooth",
    block: "center",
    inline: "nearest",
  });

  if (typeof node.focus === "function") {
    node.focus({ preventScroll: true });
  }
}

function buildOrderedPages(pages, orderedPageIds) {
  const pageMap = new Map((pages || []).map((page) => [page.id, page]));
  const nextPages = [];

  (orderedPageIds || []).forEach((pageId) => {
    const page = pageMap.get(pageId);
    if (page) {
      nextPages.push(page);
      pageMap.delete(pageId);
    }
  });

  pageMap.forEach((page) => {
    nextPages.push(page);
  });

  return nextPages;
}

function getProjectLineItems(form, t) {
  const periodValue =
    form?.period_start || form?.period_end
      ? `${form?.period_start || "-"} ~ ${form?.period_end || "-"}`
      : "-";

  return [
    {
      label: t("projectDetail.projectEditorBox.contractNameLabel", {
        defaultValue: "Contract",
      }),
      value: form?.contract_name || "-",
    },
    {
      label: t("projectDetail.projectEditorBox.contractorLabel", {
        defaultValue: "Contractor",
      }),
      value: form?.contractor || "-",
    },
    {
      label: t("projectDetail.projectEditorBox.periodLabel", {
        defaultValue: "Period",
      }),
      value: periodValue,
    },
    {
      label: t("projectDetail.projectEditorBox.purposeLabel", {
        defaultValue: "Purpose",
      }),
      value: form?.purpose || "-",
    },
  ];
}

function getReportMediaItems(file, t) {
  return [
    {
      id: "visual",
      label: t("imageFileIndex.report.ultrasonicDiagnosis", {
        defaultValue: "Ultrasonic",
      }),
      src: file?.image_visual || file?.markup_image_visual || "",
    },
    {
      id: "fft",
      label: "FFT",
      src: file?.markup_image_graph || "",
    },
    {
      id: "spectrogram",
      label: "SPECTROGRAM",
      src: file?.image_spectrogram || "",
    },
    {
      id: "thermal",
      label: t("imageFileIndex.report.thermalDiagnosis", {
        defaultValue: "Thermal",
      }),
      src:
        file?.markup_image_triplespot ||
        file?.markup_image_thermal ||
        file?.image_thermal ||
        "",
    },
    {
      id: "uv",
      label: t("imageFileIndex.report.ultravioletDiagnosis", {
        defaultValue: "Ultraviolet",
      }),
      src: file?.image_uv || "",
    },
  ];
}

function getPrimaryPreviewSource(mediaItems = []) {
  return mediaItems.find((item) => item?.src)?.src || "";
}

function hasCompleteElectricalClickPoints(candidate = {}) {
  return (
    Array.isArray(candidate?.click_points) &&
    candidate.click_points.length >= 3 &&
    candidate.click_points.slice(0, 3).every((point) => Boolean(point?.value))
  );
}

function requiresElectricalTripleSpotAnalysis(file = {}) {
  return !file?.markup_image_triplespot || !hasCompleteElectricalClickPoints(file);
}

function getProjectReportPages(project, form, files, t, selectedReportCategory = "") {
  const safeFiles = Array.isArray(files) ? files : [];
  const imageCount = safeFiles.filter((file) => file?.file_type === "image").length;
  const videoCount = safeFiles.filter((file) => file?.file_type === "video").length;
  const otherCount = Math.max(safeFiles.length - imageCount - videoCount, 0);
  const totalPages = safeFiles.length;
  const electricalReadyCount =
    selectedReportCategory === "electrical_duty"
      ? safeFiles.filter((file) => !requiresElectricalTripleSpotAnalysis(file)).length
      : 0;
  const electricalPendingCount =
    selectedReportCategory === "electrical_duty"
      ? Math.max(totalPages - electricalReadyCount, 0)
      : 0;
  const electricalCoverageRatio =
    selectedReportCategory === "electrical_duty" && totalPages > 0
      ? electricalReadyCount / totalPages
      : 0;
  const electricalCoveragePercent = Math.round(electricalCoverageRatio * 100);
  const projectLineItems = getProjectLineItems(form, t);
  const periodValue =
    form?.period_start || form?.period_end
      ? `${form?.period_start || "-"} ~ ${form?.period_end || "-"}`
      : "-";
  const projectName =
    form?.project_name ||
    project?.project_name ||
    t("projectDetail.projectReportEditor.defaultProjectTitle", {
      defaultValue: "Project Report",
    });

  const projectSummary = {
    title: projectName,
    lineItems: projectLineItems,
    cover: {
      title:
        form?.contract_name ||
        project?.contract_name ||
        projectName,
      contractor: form?.contractor || "-",
      period: periodValue,
    },
    lines: projectLineItems.map(
      (item) => `${item.label}: ${item.value}`,
    ),
    stats: [
      {
        label: t("projectDetail.projectReportEditor.totalPagesLabel", {
          defaultValue: "Pages",
        }),
        value: String(totalPages),
      },
      {
        label: t("projectDetail.projectReportEditor.imageFiles", {
          defaultValue: "Images",
        }),
        value: String(imageCount),
      },
      {
        label: t("projectDetail.projectReportEditor.videoFiles", {
          defaultValue: "Videos",
        }),
        value: String(videoCount),
      },
    ],
    chart: {
      mode: selectedReportCategory === "electrical_duty" ? "electrical_duty" : "default",
      totalFiles: safeFiles.length,
      totalPages,
      imageCount,
      videoCount,
      otherCount,
      readyCount: electricalReadyCount,
      pendingCount: electricalPendingCount,
      coverageRatio: electricalCoverageRatio,
      centerValue:
        selectedReportCategory === "electrical_duty"
          ? `${electricalCoveragePercent}%`
          : String(totalPages),
      centerLabel:
        selectedReportCategory === "electrical_duty"
          ? t("projectDetail.projectReportEditor.electricalCoverageLabel", {
              defaultValue: "3-Phase Coverage",
            })
          : t("projectDetail.projectReportEditor.totalPagesLabel", {
              defaultValue: "Pages",
            }),
    },
    legend:
      selectedReportCategory === "electrical_duty"
        ? [
            {
              label: t("projectDetail.projectReportEditor.electricalCoverageReady", {
                defaultValue: "3-phase ready",
              }),
              value: String(electricalReadyCount),
              toneClassName: "is-ready",
            },
            {
              label: t("projectDetail.projectReportEditor.electricalCoveragePending", {
                defaultValue: "Analysis needed",
              }),
              value: String(electricalPendingCount),
              toneClassName: "is-pending",
            },
          ]
        : [
            {
              label: t("projectDetail.projectReportEditor.imageFiles", {
                defaultValue: "Images",
              }),
              value: String(imageCount),
              toneClassName: "is-image",
            },
            {
              label: t("projectDetail.projectReportEditor.videoFiles", {
                defaultValue: "Videos",
              }),
              value: String(videoCount),
              toneClassName: "is-video",
            },
            {
              label: t("projectDetail.projectReportEditor.otherFiles", {
                defaultValue: "Others",
              }),
              value: String(otherCount),
              toneClassName: "is-other",
            },
          ],
  };

  const pages = safeFiles.map((file, index) => {
    const requiresElectricalAnalysis =
      selectedReportCategory === "electrical_duty"
        ? requiresElectricalTripleSpotAnalysis(file)
        : false;
    const mediaItems = getReportMediaItems(file, t);
    const previewImage = getPrimaryPreviewSource(mediaItems);

    return {
      id: file?.upload_id || `project-file-${index + 1}`,
      title:
        file?.clean_file_name ||
        file?.file_name ||
        t("projectDetail.projectReportEditor.fileFallbackTitle", {
          defaultValue: "Untitled file",
        }),
      subtitle:
        file?.facility_name ||
        file?.facility_type ||
        t("projectDetail.fileList.table.facilityName", {
          defaultValue: "Facility",
        }),
      typeLabel: file?.file_type || "-",
      pageNumber: index + 1,
      meta: [
        {
          label: t("projectDetail.fileList.table.facilityType", {
            defaultValue: "Facility type",
          }),
          value: file?.facility_type || "-",
        },
        {
          label: t("projectDetail.fileList.table.facilityName", {
            defaultValue: "Facility name",
          }),
          value: file?.facility_name || "-",
        },
        {
          label: t("projectDetail.fileList.table.location", {
            defaultValue: "Location",
          }),
          value: file?.location || "-",
        },
        {
          label: t("imageFileIndex.report.shootingDate", {
            defaultValue: "Shooting date",
          }),
          value: file?.shooting_date || "-",
        },
      ],
      note:
        file?.inspection_result ||
        file?.fusion_result ||
        t("projectDetail.projectReportEditor.noDiagnosisNote", {
          defaultValue: "No diagnosis note.",
        }),
      requiresElectricalAnalysis,
      previewImage,
      mediaItems,
      file,
    };
  });

  if (pages.length) {
    return {
      projectSummary,
      pages,
    };
  }

  return {
    projectSummary,
    pages: [
      {
        id: "project-report-empty",
        title: t("projectDetail.projectReportEditor.emptyTitle", {
          defaultValue: "No files available",
        }),
        subtitle: projectName,
        typeLabel: "-",
        pageNumber: 1,
        meta: [],
        note: t("projectDetail.projectReportEditor.emptyDescription", {
          defaultValue: "There are no files to preview in this report yet.",
        }),
        previewImage: "",
        mediaItems: [],
        isEmpty: true,
      },
    ],
  };
}

function buildProjectReportChartStyle(chart = {}) {
  if (chart.mode === "electrical_duty") {
    const readyAngle = (Math.max(Number(chart.coverageRatio) || 0, 0) % 1) * 360;

    return {
      background: `conic-gradient(
        #0f766e 0deg ${readyAngle}deg,
        #fda4af ${readyAngle}deg 360deg
      )`,
    };
  }

  const total = Math.max(Number(chart.totalFiles) || 0, 1);
  const imageAngle = ((Number(chart.imageCount) || 0) / total) * 360;
  const videoAngle = ((Number(chart.videoCount) || 0) / total) * 360;
  const otherAngle = Math.max(360 - imageAngle - videoAngle, 0);

  return {
    background: `conic-gradient(
      #2563eb 0deg ${imageAngle}deg,
      #0f766e ${imageAngle}deg ${imageAngle + videoAngle}deg,
      #cbd5e1 ${imageAngle + videoAngle}deg ${imageAngle + videoAngle + otherAngle}deg
    )`,
  };
}

function getReportCategoryLabel(selectedReportCategory, t) {
  return (
    t(`projectDetail.projectReportEditor.reportCategory.${selectedReportCategory}`, {
      defaultValue:
        REPORT_CATEGORY_OPTIONS.find((option) => option.id === selectedReportCategory)?.label || "",
    }) || ""
  );
}

function getExportTypeLabel(selectedExportType, t) {
  return (
    t(`projectDetail.projectReportEditor.exportType.${selectedExportType}`, {
      defaultValue:
        EXPORT_TYPE_OPTIONS.find((option) => option.id === selectedExportType)?.label || "",
    }) || ""
  );
}

function clampProgressValue(value) {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, Math.round(value)));
}

function ProjectReportCoverPage({
  projectSummary,
  reportLabel,
  t,
}) {
  const coverTitle =
    projectSummary?.cover?.title ||
    projectSummary?.title ||
    t("projectDetail.projectReportEditor.defaultProjectTitle", {
      defaultValue: "Project Report",
    });

  return (
    <div
      className="projectReportCoverPage"
      data-report-cover="true"
    >
      <div className="projectReportCoverPageInner">
        <div className="projectReportCoverPageHero">
          <div className="projectReportCoverPageTitle">{coverTitle}</div>
          <div className="projectReportCoverPageReportType">
            {reportLabel ||
              t("projectDetail.projectReportEditor.defaultReportLabel", {
                defaultValue: "Inspection Report",
              })}
          </div>
        </div>

        <div className="projectReportCoverPageBottom">
          <div className="projectReportCoverPageMeta">
            <div className="projectReportCoverPageMetaRow">
              <div className="projectReportCoverPageMetaLabel">
                {t("projectDetail.projectEditorBox.contractorLabel", {
                  defaultValue: "Contractor",
                })}
              </div>
              <div className="projectReportCoverPageMetaValue">
                {projectSummary?.cover?.contractor || "-"}
              </div>
            </div>
            <div className="projectReportCoverPageMetaRow">
              <div className="projectReportCoverPageMetaLabel">
                {t("projectDetail.projectEditorBox.periodLabel", {
                  defaultValue: "Period",
                })}
              </div>
              <div className="projectReportCoverPageMetaValue">
                {projectSummary?.cover?.period || "-"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function getProjectReportListPreviewIcon(selectedReportCategory = "") {
  if (selectedReportCategory === "ud1000_fusion") {
    return "/images/ud1000_report_icon.png";
  }

  if (selectedReportCategory === "electrical_duty") {
    return "/images/electicalsaftey_report_icon.png";
  }

  return "/images/report_placeholder.png";
}

function ProjectReportListPreviewCard({
  page,
  order,
  selectedReportCategory = "",
}) {
  if (page?.isUd1000TemplateCover) {
    return (
      <div className="projectReportEditorListPreviewCard is-coverPage">
        <div className="projectReportEditorListPreviewCoverBadge">COVER</div>
        <div className="projectReportEditorListPreviewCoverPageNumber">
          {order}
        </div>
        <div className="projectReportEditorListPreviewCoverTitle">
          {page?.title || "표지"}
        </div>
      </div>
    );
  }

  const iconSrc = getProjectReportListPreviewIcon(selectedReportCategory);

  return (
    <div className="projectReportEditorListPreviewCard">
      <img
        className="projectReportEditorListPreviewCardImage"
        src={iconSrc}
        alt={page?.title || "Report preview"}
        loading="lazy"
      />
      <div className="projectReportEditorListItemOrder">{order}</div>
      <div className="projectReportEditorListPreviewTitle">{page?.title}</div>
    </div>
  );
}

export default function ProjectReportEditorModal({
  open,
  embedded = false,
  projectId,
  userId,
  project,
  form,
  files,
  loading = false,
  onClose,
  t,
}) {
  const { i18n } = useTranslation();
  const currentUser = useAppStore((s) => s.user || null);
  const showAlert = useAppStore((s) => s.showAlert);
  const currentLanguage = i18n.resolvedLanguage ?? i18n.language ?? "";
  const canRenderHwp = isKoreanLanguage(currentLanguage);
  const canRenderElectricalDuty = isKoreanLanguage(currentLanguage);
  const [selectedPageId, setSelectedPageId] = useState(null);
  const [selectedReportCategory, setSelectedReportCategory] = useState("");
  const [selectedExportType, setSelectedExportType] = useState("");
  const [isProjectSummaryOpen, setIsProjectSummaryOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgressPercent, setExportProgressPercent] = useState(0);
  const [exportProgressDetail, setExportProgressDetail] = useState("");
  const [includeCoverPage, setIncludeCoverPage] = useState(true);
  const [useDetailConditions, setUseDetailConditions] = useState(false);
  const [loadRequest, setLoadRequest] = useState(null);
  const [loadedRequest, setLoadedRequest] = useState(null);
  const [reportDataError, setReportDataError] = useState("");
  const [coverConfirmed, setCoverConfirmed] = useState(false);
  const listScope = loadRequest?.listScope;
  const scopeKey = JSON.stringify([projectId, selectedReportCategory]);
  const scopeConfirmed = Boolean(selectedReportCategory);
  const conditionsConfirmed = scopeConfirmed && loadRequest?.scopeKey === scopeKey;
  const [ud1000ReportFiles, setUd1000ReportFiles] = useState([]);
  const [ud1000ReportLoading, setUd1000ReportLoading] = useState(false);
  const [ud1000ReportFilterSelection, setUd1000ReportFilterSelection] =
    useState(null);
  const [electricalSafetyReportFiles, setElectricalSafetyReportFiles] = useState([]);
  const [electricalSafetyReportLoading, setElectricalSafetyReportLoading] =
    useState(false);
  const [isElectricalTripleSpotModalOpen, setIsElectricalTripleSpotModalOpen] =
    useState(false);
  const [electricalTripleSpotCircles, setElectricalTripleSpotCircles] =
    useState(EMPTY_ELECTRICAL_CIRCLES);
  const [isElectricalTripleSpotSaving, setIsElectricalTripleSpotSaving] =
    useState(false);
  const [electricalTripleSpotPageId, setElectricalTripleSpotPageId] =
    useState(null);
  const [orderedPageIds, setOrderedPageIds] = useState([]);
  const [excludedPageIds, setExcludedPageIds] = useState([]);
  const exportRef = useRef(null);
  const currentFrameRef = useRef(null);
  const itemRefs = useRef(new Map());
  const electricalThermalCaptureTarget = useRef(null);
  const electricalTripleSpotCaptureTarget = useRef(null);
  const previousItemRectsRef = useRef(new Map());
  const busyShieldRef = useRef(null);
  const [currentPreviewFrameHeight, setCurrentPreviewFrameHeight] = useState(null);
  const availableReportCategoryOptions = useMemo(
    () =>
      REPORT_CATEGORY_OPTIONS.filter(
        (option) => option.id !== "electrical_duty" || canRenderElectricalDuty,
      ),
    [canRenderElectricalDuty],
  );
  const defaultReportCategory = useMemo(
    () => getDefaultReportCategory(canRenderElectricalDuty),
    [canRenderElectricalDuty],
  );
  const availableReportCategoryIds = useMemo(
    () => availableReportCategoryOptions.map((option) => option.id),
    [availableReportCategoryOptions],
  );
  const normalizedUd1000ReportFilterSelection = useMemo(
    () => normalizeUd1000ReportFilterSelection(ud1000ReportFilterSelection),
    [ud1000ReportFilterSelection],
  );
  const ud1000ReportFilterCount = countUd1000ReportFilterSelection(
    normalizedUd1000ReportFilterSelection,
  );
  const hasUd1000ReportLoadSelection = ud1000ReportFilterCount > 0;
  const ud1000ReportFilterGroups = useMemo(
    () =>
      buildProjectDetailFilterGroups({
        t,
        i18n,
        includeAll: true,
        allGroupKey: UD1000_REPORT_ALL_FILTER_KEY,
        allValue: UD1000_REPORT_ALL_FILTER_VALUE,
      }),
    [i18n, t],
  );
  const activeFiles =
    selectedReportCategory === "ud1000_fusion"
      ? ud1000ReportFiles
      : selectedReportCategory === "electrical_duty"
        ? electricalSafetyReportFiles
        : files;
  const reportDataLoading =
    selectedReportCategory === "ud1000_fusion"
      ? ud1000ReportLoading
      : selectedReportCategory === "electrical_duty"
        ? electricalSafetyReportLoading
        : false;

  const { projectSummary, pages } = useMemo(
    () => getProjectReportPages(project, form, activeFiles, t, selectedReportCategory),
    [project, form, activeFiles, t, selectedReportCategory],
  );

  const orderedPages = useMemo(
    () => buildOrderedPages(pages, orderedPageIds),
    [pages, orderedPageIds],
  );
  const reportSourcePages = useMemo(
    () => orderedPages.filter((page) => !page.isEmpty),
    [orderedPages],
  );
  const hasReportSourcePages = reportSourcePages.length > 0;
  const includedOrderedPages = useMemo(
    () => orderedPages.filter((page) => !page.isEmpty && !excludedPageIds.includes(page.id)),
    [excludedPageIds, orderedPages],
  );

  const selectedPage =
    orderedPages.find((page) => page.id === selectedPageId) || orderedPages[0];
  const shouldRenderCurrentFrame =
    loading || reportDataLoading || !selectedPage?.isEmpty;
  const shouldRenderReportMain =
    Boolean(selectedReportCategory) &&
    (loading || reportDataLoading || hasReportSourcePages);
  const shouldShowReportFileGuide =
    Boolean(selectedReportCategory) &&
    !loading &&
    !reportDataLoading &&
    !hasReportSourcePages;
  const electricalTripleSpotPage =
    orderedPages.find((page) => page.id === electricalTripleSpotPageId) || null;
  const exportableOrderedPages = useMemo(
    () =>
      includedOrderedPages.filter(
        (page) =>
          !(
            selectedReportCategory === "electrical_duty" &&
            page.requiresElectricalAnalysis
          ),
      ),
    [includedOrderedPages, selectedReportCategory],
  );
  const ud1000PdfCoverPages = useMemo(
    () =>
      selectedReportCategory === "ud1000_fusion" && includeCoverPage
        ? buildUd1000PdfCoverPages({
            project,
            form,
            reportPages: exportableOrderedPages,
            i18n,
          })
        : [],
    [exportableOrderedPages, form, i18n, includeCoverPage, project, selectedReportCategory],
  );
  const electricalPdfCoverPages = useMemo(() => {
    if (
      selectedReportCategory !== "electrical_duty" ||
      selectedExportType !== "pdf" ||
      !includeCoverPage
    ) {
      return [];
    }

    const [introCoverPage] = buildUd1000PdfCoverPages({
      project,
      form,
      reportPages: exportableOrderedPages,
      i18n,
    });

    if (!introCoverPage) return [];

    return [
      {
        ...introCoverPage,
        id: "electrical-duty-cover-intro",
        headline: t("projectDetail.projectReportEditor.electricalDutyCover.introHeadline", {
          defaultValue: "\uC804\uAE30 \uC9C1\uBB34 \uACE0\uC2DC \uACB0\uACFC\uBCF4\uACE0\uC11C",
        }),
      },
    ];
  }, [
    exportableOrderedPages,
    form,
    i18n,
    includeCoverPage,
    project,
    selectedExportType,
    selectedReportCategory,
    t,
  ]);
  const pendingElectricalPages = useMemo(
    () =>
      selectedReportCategory === "electrical_duty"
        ? includedOrderedPages.filter((page) => page.requiresElectricalAnalysis)
        : [],
    [includedOrderedPages, selectedReportCategory],
  );
  const projectReportName =
    form?.project_name ||
    project?.project_name ||
    t("projectDetail.projectReportEditor.defaultFileName", {
      defaultValue: "project-report",
    });
  const selectionDescriptionText = t(
    "projectDetail.projectReportEditor.selectionDescriptionWithName",
    {
      projectName: projectReportName,
      defaultValue: `${projectReportName} report builder`,
    },
  );
  const requiresReportScopeSelection = selectedReportCategory === "ud1000_fusion";
  const hasReadyReportScope =
    conditionsConfirmed && loadedRequest === loadRequest && !reportDataLoading && !reportDataError && activeFiles.length > 0 &&
    (!requiresReportScopeSelection || hasUd1000ReportLoadSelection);
  const selectionGuideText = !selectedReportCategory
    ? t("projectDetail.projectReportEditor.selectReportGuide", {
        defaultValue: "보고서 종류를 선택해 주세요.",
      })
    : reportDataLoading ? "선택한 조건의 파일을 불러오고 있습니다."
    : !hasReadyReportScope
      ? "대상 파일을 확인해 주세요. 상세 조건을 수정했다면 적용하거나 ‘상세 조건 사용’을 해제해 주세요."
    : !selectedExportType
      ? t("projectDetail.projectReportEditor.selectFormatGuide", {
          defaultValue: "출력 파일 형식을 선택해 주세요.",
        })
      : t("projectDetail.projectReportEditor.selectionSummary", {
          reportLabel: getReportCategoryLabel(selectedReportCategory, t),
          exportLabel: getExportTypeLabel(selectedExportType, t),
          defaultValue: `${getReportCategoryLabel(selectedReportCategory, t)} / ${getExportTypeLabel(
            selectedExportType,
            t,
          )}`,
        });
  const reportFileGuideTitle =
    selectedReportCategory === "ud1000_fusion" && !hasUd1000ReportLoadSelection
      ? t("projectDetail.projectReportEditor.fileGuide.selectFilterTitle", {
          defaultValue: "Select report files to load.",
        })
      : t("projectDetail.projectReportEditor.fileGuide.emptyTitle", {
          defaultValue: "No report files selected.",
        });
  const reportFileGuideDescription =
    selectedReportCategory === "ud1000_fusion"
      ? hasUd1000ReportLoadSelection
        ? t("projectDetail.projectReportEditor.fileGuide.emptyFilterDescription", {
            defaultValue:
              "No files match the selected conditions. Adjust the filters or choose All, then load again.",
          })
        : t("projectDetail.projectReportEditor.fileGuide.selectFilterDescription", {
            defaultValue:
              "Choose All or select detailed conditions, then press Load to bring files into the report.",
          })
      : t("projectDetail.projectReportEditor.fileGuide.emptyDescription", {
          defaultValue:
            "Upload project files or choose another report type before exporting.",
        });
  const canExportPdf =
    !loading &&
    !reportDataLoading &&
    !isExporting &&
    (selectedReportCategory === "electrical_duty"
      ? includedOrderedPages.length > 0
      : exportableOrderedPages.length > 0) &&
    Boolean(selectedReportCategory) &&
    selectedExportType === "pdf";
  const canExportExcel =
    !loading &&
    !reportDataLoading &&
    !isExporting &&
    (selectedReportCategory === "electrical_duty"
      ? includedOrderedPages.length > 0
      : exportableOrderedPages.length > 0) &&
    ["ud1000_fusion", "electrical_duty"].includes(selectedReportCategory) &&
    selectedExportType === "excel";
  const canExportHwp =
    canRenderHwp &&
    !loading &&
    !reportDataLoading &&
    !isExporting &&
    (selectedReportCategory === "electrical_duty"
      ? includedOrderedPages.length > 0
      : exportableOrderedPages.length > 0) &&
    ["ud1000_fusion", "electrical_duty"].includes(selectedReportCategory) &&
    selectedExportType === "hwp";
  const needsCoverChoice = (selectedReportCategory === "electrical_duty" && selectedExportType === "pdf") ||
    (selectedReportCategory === "ud1000_fusion" && ["pdf", "excel", "hwp"].includes(selectedExportType));
  const currentStep = !selectedReportCategory || !hasReadyReportScope ? 1 : !selectedExportType ? 2 : needsCoverChoice && !coverConfirmed ? 3 : 4;
  const stepClass = step => currentStep === step ? "is-currentStep" : currentStep < step ? "is-lockedStep" : "is-completeStep";
  const stepStatus = step => <small>{currentStep === step ? "선택 중" : currentStep < step ? "대기" : "완료"}</small>;
  const canExport = hasReadyReportScope && (!needsCoverChoice || coverConfirmed) && (canExportPdf || canExportExcel || canExportHwp);
  const isInteractionLocked = isExporting;
  const canSelectCover = hasReadyReportScope && needsCoverChoice && !isInteractionLocked;
  const exportActionLabel = isExporting
    ? selectedExportType === "excel"
      ? t("projectDetail.projectReportEditor.exportingExcel", {
          defaultValue: "Exporting Excel...",
        })
      : selectedExportType === "hwp"
        ? t("projectDetail.projectReportEditor.exportingHwp", {
            defaultValue: "Exporting HWP...",
          })
      : t("projectDetail.projectReportEditor.exporting", {
          defaultValue: "Exporting PDF...",
        })
    : selectedExportType === "excel"
      ? t("projectDetail.projectReportEditor.exportExcel", {
          defaultValue: "Export Excel",
        })
      : selectedExportType === "hwp"
        ? t("projectDetail.projectReportEditor.exportHwp", {
            defaultValue: "Export HWP",
          })
      : t("projectDetail.projectReportEditor.exportPdf", {
          defaultValue: "Export PDF",
        });
  const exportProgressText = exportProgressDetail || exportActionLabel;

  function updateExportProgress(percent, detail = "") {
    const nextPercent = clampProgressValue(percent);

    setExportProgressPercent((prev) => (prev === nextPercent ? prev : nextPercent));
    setExportProgressDetail((prev) => (prev === detail ? prev : detail));
  }

  function resetExportProgress() {
    setExportProgressPercent(0);
    setExportProgressDetail("");
  }

  function getExportProgressDetail(stage, current, total) {
    if (stage === "worksheet" || stage === "page") {
      return t("projectDetail.projectReportEditor.progress.processingCount", {
        current,
        total,
        defaultValue: `Processing ${current || 0}/${total || 0}...`,
      });
    }

    if (stage === "template-ready" || stage === "capturing") {
      return t("projectDetail.projectReportEditor.progress.rendering", {
        defaultValue: "Rendering report...",
      });
    }

    if (stage === "packaging") {
      return t("projectDetail.projectReportEditor.progress.packaging", {
        defaultValue: "Packaging file...",
      });
    }

    if (stage === "saving" || stage === "complete") {
      return t("projectDetail.projectReportEditor.progress.finishing", {
        defaultValue: "Finishing download...",
      });
    }

    return t("projectDetail.projectReportEditor.progress.preparing", {
      defaultValue: "Preparing export...",
    });
  }

  function showExportSuccessAlert() {
    showAlert?.({
      text: t("projectDetail.projectReportEditor.exportSuccess", {
        exportLabel: getExportTypeLabel(selectedExportType, t),
        defaultValue: `${getExportTypeLabel(selectedExportType, t)} export completed successfully.`,
      }),
      success: true,
      autoClose: true,
    });
  }

  function showExportFailedAlert() {
    showAlert?.({
      text: t("projectDetail.projectReportEditor.exportFailed", {
        exportLabel: getExportTypeLabel(selectedExportType, t),
        defaultValue: `${getExportTypeLabel(selectedExportType, t)} export failed.`,
      }),
      success: false,
      autoClose: true,
    });
  }

  function handleRequestClose() {
    if (isInteractionLocked) return;
    onClose?.();
  }

  function handleUd1000ReportFilterChange(nextSelection, nextScope) {
    if (!scopeConfirmed || isInteractionLocked) return;
    setUd1000ReportFilterSelection(
      normalizeUd1000ReportFilterSelection(nextSelection),
    );
    setSelectedExportType("");
    setCoverConfirmed(false);
    setReportDataError("");
    setLoadRequest({ scopeKey, listScope: nextScope });
  }

  function invalidateReportConditions() {
    setLoadRequest(null);
    setUd1000ReportFilterSelection(null);
    setSelectedExportType("");
    setCoverConfirmed(false);
    setReportDataError("");
  }

  useLayoutEffect(() => {
    if (!open) return;
    setUseDetailConditions(false);
    setLoadRequest(selectedReportCategory ? { scopeKey } : null);
    setLoadedRequest(null);
    setUd1000ReportFilterSelection(selectedReportCategory ? getUd1000ReportAllFilterSelection() : null);
    setReportDataError("");
    setCoverConfirmed(false);
    setUd1000ReportFiles([]);
    setElectricalSafetyReportFiles([]);
    setSelectedExportType("");
    setSelectedPageId(null);
    setExcludedPageIds([]);
  }, [open, scopeKey, selectedReportCategory]);

  useEffect(() => {
    if (!open) return undefined;

    if (!embedded) document.body.classList.add("reportEditModalOpen");
    setSelectedPageId(null);
    setSelectedReportCategory(defaultReportCategory);
    setSelectedExportType("");
    setIsProjectSummaryOpen(false);
    setIncludeCoverPage(true);
    setUseDetailConditions(false);
    setLoadedRequest(null);
    setCoverConfirmed(false);
    setReportDataError("");
    setUd1000ReportFiles([]);
    setUd1000ReportLoading(false);
    setElectricalSafetyReportFiles([]);
    setIsElectricalTripleSpotModalOpen(false);
    setElectricalTripleSpotCircles(EMPTY_ELECTRICAL_CIRCLES);
    setIsElectricalTripleSpotSaving(false);
    setElectricalTripleSpotPageId(null);
    setOrderedPageIds([]);
    setExcludedPageIds([]);
    resetExportProgress();
    previousItemRectsRef.current = new Map();

    return () => {
      if (!embedded) document.body.classList.remove("reportEditModalOpen");
    };
  }, [defaultReportCategory, embedded, open]);

  useEffect(() => {
    if (!open) return;

    if (!selectedReportCategory) {
      if (!defaultReportCategory) return;

      setSelectedReportCategory(defaultReportCategory);
      setSelectedExportType("");
      return;
    }

    if (availableReportCategoryIds.includes(selectedReportCategory)) {
      return;
    }

    const nextCategory = defaultReportCategory || "";

    if (selectedReportCategory === nextCategory) return;

    setSelectedReportCategory(nextCategory);
    setSelectedExportType("");
  }, [
    availableReportCategoryIds,
    defaultReportCategory,
    open,
    selectedReportCategory,
  ]);

  useEffect(() => {
    if (!isInteractionLocked) return;
    busyShieldRef.current?.focus();
  }, [isInteractionLocked]);

  useLayoutEffect(() => {
    const frameElement = currentFrameRef.current;
    if (!open || !frameElement) return undefined;

    let frameId = 0;

    const measure = () => {
      const nextHeight = Math.max(Math.floor(frameElement.clientHeight), 0);

      setCurrentPreviewFrameHeight((prev) => {
        if (Math.abs((prev ?? 0) - nextHeight) < 1) {
          return prev;
        }

        return nextHeight;
      });
    };

    const scheduleMeasure = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(measure);
    };

    scheduleMeasure();

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            scheduleMeasure();
          });

    resizeObserver?.observe(frameElement);
    window.addEventListener("resize", scheduleMeasure);

    return () => {
      window.cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
    };
  }, [open, selectedPage?.id, selectedReportCategory, loading, reportDataLoading]);

  useEffect(() => {
    const pageIds = pages.map((page) => page.id);

    setOrderedPageIds((prev) => {
      const nextIds = buildOrderedPages(
        pageIds.map((id) => ({ id })),
        prev,
      ).map((page) => page.id);

      if (
        prev.length === nextIds.length &&
        prev.every((pageId, index) => pageId === nextIds[index])
      ) {
        return prev;
      }

      return nextIds;
    });

    setSelectedPageId((prev) => (pageIds.includes(prev) ? prev : pageIds[0] || null));
    setExcludedPageIds((prev) => prev.filter((pageId) => pageIds.includes(pageId)));
  }, [pages]);

  useEffect(() => {
    if (!open || selectedReportCategory !== "ud1000_fusion" || !projectId) {
      return undefined;
    }

    if (!hasUd1000ReportLoadSelection || !conditionsConfirmed) {
      setUd1000ReportLoading(false);
      setUd1000ReportFiles([]);
      return undefined;
    }

    let cancelled = false;
    const { sortKey, sortDir } = getFileSortPref(userId);
    const reportFilters = getUd1000ReportQueryFilters(
      normalizedUd1000ReportFilterSelection,
    );

    setUd1000ReportLoading(true);
    setUd1000ReportFiles([]);

    (async () => {
      try {
        const result = await getProjectUd1000ReportData(projectId, {
          listScope,
          sortKey,
          sortDir,
          fileType: reportFilters.fileType,
          facilityType: reportFilters.facilityType,
          fusionResult: reportFilters.fusionResult,
        });

        if (cancelled) return;

        if (!result?.success) { setReportDataError(result?.message || "파일을 불러오지 못했습니다. 다시 시도해 주세요."); return; }
        const nextFiles = Array.isArray(result?.files) ? result.files : [];
        setUd1000ReportFiles(nextFiles);
        setLoadedRequest(loadRequest);
      } catch {
        if (!cancelled) setReportDataError("파일을 불러오지 못했습니다. 다시 시도해 주세요.");
      } finally {
        if (!cancelled) {
          setUd1000ReportLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    hasUd1000ReportLoadSelection,
    conditionsConfirmed,
    loadRequest,
    listScope,
    normalizedUd1000ReportFilterSelection,
    open,
    projectId,
    selectedReportCategory,
    userId,
  ]);

  useEffect(() => {
    if (!open || selectedReportCategory !== "electrical_duty" || !projectId) {
      setIsElectricalTripleSpotModalOpen(false);
      setElectricalTripleSpotPageId(null);
      return undefined;
    }

    if (!conditionsConfirmed) {
      setElectricalSafetyReportLoading(false);
      setElectricalSafetyReportFiles([]);
      return undefined;
    }

    let cancelled = false;
    const { sortKey, sortDir } = getFileSortPref(userId);

    setElectricalSafetyReportLoading(true);
    setElectricalSafetyReportFiles([]);

    (async () => {
      try {
        const result = await getProjectElectricalSafetyReportData(projectId, {
          listScope,
          sortKey,
          sortDir,
        });

        if (cancelled) return;

        if (!result?.success) { setReportDataError(result?.message || "파일을 불러오지 못했습니다. 다시 시도해 주세요."); return; }
        const nextFiles = Array.isArray(result?.files) ? result.files : [];
        setElectricalSafetyReportFiles(nextFiles);
        setLoadedRequest(loadRequest);
      } catch {
        if (!cancelled) setReportDataError("파일을 불러오지 못했습니다. 다시 시도해 주세요.");
      } finally {
        if (!cancelled) {
          setElectricalSafetyReportLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, projectId, selectedReportCategory, userId, listScope, conditionsConfirmed, loadRequest]);

  useEffect(() => {
    if (
      !open ||
      loading ||
      reportDataLoading ||
      selectedReportCategory !== "electrical_duty" ||
      !selectedPage?.requiresElectricalAnalysis
    ) {
      return;
    }

    setElectricalTripleSpotPageId(selectedPage.id);
    setElectricalTripleSpotCircles([
      selectedPage.file?.click_points?.[0] ?? undefined,
      selectedPage.file?.click_points?.[1] ?? undefined,
      selectedPage.file?.click_points?.[2] ?? undefined,
    ]);
    setIsElectricalTripleSpotModalOpen(true);
  }, [
    open,
    loading,
    reportDataLoading,
    selectedReportCategory,
    selectedPage?.id,
    selectedPage?.file?.click_points,
    selectedPage?.requiresElectricalAnalysis,
  ]);

  useLayoutEffect(() => {
    const nextRects = new Map();

    orderedPages.forEach((page) => {
      const node = itemRefs.current.get(page.id);
      if (node) {
        nextRects.set(page.id, node.getBoundingClientRect());
      }
    });

    const previousRects = previousItemRectsRef.current;

    nextRects.forEach((nextRect, pageId) => {
      const previousRect = previousRects.get(pageId);
      const node = itemRefs.current.get(pageId);

      if (!previousRect || !node) return;

      const deltaY = previousRect.top - nextRect.top;
      if (Math.abs(deltaY) < 1) return;

      node.style.transition = "none";
      node.style.transform = `translateY(${deltaY}px)`;

      window.requestAnimationFrame(() => {
        node.style.transition =
          "transform 260ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 180ms ease, border-color 180ms ease";
        node.style.transform = "";
      });
    });

    previousItemRectsRef.current = nextRects;
  }, [orderedPages]);

  if (!open) return null;

  const handleExport = async () => {
    if (!canExport) return;

    if (selectedReportCategory === "electrical_duty" && pendingElectricalPages.length > 0) {
      const nextPendingPage = pendingElectricalPages[0];
      const pendingNode = itemRefs.current.get(nextPendingPage?.id);

      if (nextPendingPage) {
        openElectricalTripleSpotModal(nextPendingPage);
      }

      window.requestAnimationFrame(() => {
        scrollNodeIntoView(pendingNode);
      });

      showAlert?.({
        text: t("projectDetail.projectReportEditor.pendingElectricalExportBlocked", {
          defaultValue:
            "Report download is unavailable until the selected electrical duty file completes Triple-Spot Analysis.",
        }),
        success: false,
        autoClose: true,
      });
      return;
    }

    const exportFileName =
      projectReportName.replace(/[\\/:*?"<>|]+/g, " ").trim() || "project-report";
    const orderedUploadIds = exportableOrderedPages
      .map((page) => page?.file?.upload_id)
      .filter(Boolean);

    setIsExporting(true);
    updateExportProgress(
      8,
      t("projectDetail.projectReportEditor.progress.preparing", {
        defaultValue: "Preparing export...",
      }),
    );
    try {
      if (selectedExportType === "excel") {
        if (selectedReportCategory === "electrical_duty") {
          await downloadProjectElectricalSafetyExcelReport({
            pages: exportableOrderedPages,
            projectReportName: exportFileName,
            currentUser,
            onProgress: ({ percent, stage, current, total }) => {
              updateExportProgress(percent, getExportProgressDetail(stage, current, total));
            },
          });
        } else {
          await downloadProjectUd1000ExcelReport({
            pages: exportableOrderedPages,
            coverPages: includeCoverPage ? ud1000PdfCoverPages : [],
            projectReportName: exportFileName,
            t,
            i18n,
            onProgress: ({ percent, stage, current, total }) => {
              updateExportProgress(percent, getExportProgressDetail(stage, current, total));
            },
          });
        }
        updateExportProgress(
          100,
          t("projectDetail.projectReportEditor.progress.finishing", {
            defaultValue: "Finishing download...",
          }),
        );
        showExportSuccessAlert();
        return;
      }

      if (selectedExportType === "hwp") {
        updateExportProgress(
          24,
          t("projectDetail.projectReportEditor.progress.requesting", {
            defaultValue: "Requesting report file...",
          }),
        );
        const response =
          selectedReportCategory === "electrical_duty"
            ? await postProjectElectricalSafetyHmlReport(projectId, exportFileName, {
                orderedUploadIds,
              })
            : await postProjectUd1000HmlReport(projectId, exportFileName, {
                orderedUploadIds,
                includeCover: includeCoverPage,
              });

        if (!response?.success) {
          showAlert?.({
            text: response?.message,
            messageKey: response?.messageKey,
            messageParams: response?.messageParams,
            success: false,
            autoClose: true,
          });
          return;
        }

        updateExportProgress(
          100,
          t("projectDetail.projectReportEditor.progress.finishing", {
            defaultValue: "Finishing download...",
          }),
        );
        showAlert?.({
          text: response?.message,
          messageKey: response?.messageKey,
          messageParams: response?.messageParams,
          success: true,
          autoClose: true,
        });
        return;
      }

      if (!exportRef.current) return;

      if (
        selectedReportCategory === "ud1000_fusion" ||
        selectedReportCategory === "electrical_duty"
      ) {
        await createPagedPdfReport(
          exportRef,
          exportFileName,
          REPORT_PRINT_PDF_LAYOUT.paged,
          ({ percent, stage, current, total }) => {
            updateExportProgress(percent, getExportProgressDetail(stage, current, total));
          },
        );
        showExportSuccessAlert();
        return;
      }

        await createPdfReport(exportRef, exportFileName, undefined, ({ percent, stage }) => {
          updateExportProgress(percent, getExportProgressDetail(stage));
        });
      showExportSuccessAlert();
    } catch (error) {
      console.error("[ProjectReportEditorModal] export failed", error);
      showExportFailedAlert();
    } finally {
      setIsExporting(false);
      resetExportProgress();
    }
  };

  const selectedReportLabel = getReportCategoryLabel(selectedReportCategory, t);
  const showCoverOption =
    (["ud1000_fusion", "electrical_duty"].includes(selectedReportCategory) &&
      selectedExportType === "pdf") ||
    (selectedReportCategory === "ud1000_fusion" &&
      ["excel", "hwp"].includes(selectedExportType));
  const pdfTemplateCoverPages =
    selectedReportCategory === "electrical_duty"
      ? electricalPdfCoverPages
      : ud1000PdfCoverPages;
  const usePdfTemplateCover =
    ["ud1000_fusion", "electrical_duty"].includes(selectedReportCategory) &&
    selectedExportType === "pdf" &&
    includeCoverPage &&
    pdfTemplateCoverPages.length > 0;
  const coverPageCount = (() => {
    if (!includeCoverPage) return 0;

    if (selectedReportCategory === "ud1000_fusion") {
      return ud1000PdfCoverPages.length;
    }

    if (selectedReportCategory === "electrical_duty" && selectedExportType === "pdf") {
      return electricalPdfCoverPages.length;
    }

    return 0;
  })();
  const reportSourceItemCount = reportSourcePages.length;
  const excludedExportItemCount = Math.max(
    reportSourceItemCount - exportableOrderedPages.length,
    0,
  );
  const totalGeneratedPageCount = exportableOrderedPages.length + coverPageCount;
  const availableExportTypeOptions = EXPORT_TYPE_OPTIONS.filter((option) => {
    if (option.id === "pdf") return Boolean(selectedReportCategory);
    if (option.id === "excel") {
      return ["ud1000_fusion", "electrical_duty"].includes(selectedReportCategory);
    }
    if (option.id === "hwp") {
      return (
        canRenderHwp &&
        ["ud1000_fusion", "electrical_duty"].includes(selectedReportCategory)
      );
    }
    return Boolean(selectedReportCategory);
  });

  function openElectricalTripleSpotModal(page) {
    if (isInteractionLocked) return;
    if (selectedReportCategory !== "electrical_duty") return;
    if (!page?.file) return;

    setSelectedPageId(page.id);
    setElectricalTripleSpotPageId(page.id);
    setElectricalTripleSpotCircles([
      page.file?.click_points?.[0] ?? undefined,
      page.file?.click_points?.[1] ?? undefined,
      page.file?.click_points?.[2] ?? undefined,
    ]);
    setIsElectricalTripleSpotModalOpen(true);
  }

  function closeElectricalTripleSpotModal() {
    if (isInteractionLocked) return;
    if (isElectricalTripleSpotSaving) return;
    setIsElectricalTripleSpotModalOpen(false);
    setElectricalTripleSpotPageId(null);
  }

  function selectProjectReportPage(pageId) {
    if (isInteractionLocked) return;
    const nextPage = orderedPages.find((page) => page.id === pageId);
    setSelectedPageId(pageId);

    if (
      selectedReportCategory === "electrical_duty" &&
      nextPage?.requiresElectricalAnalysis
    ) {
      openElectricalTripleSpotModal(nextPage);
      return;
    }

    if (isElectricalTripleSpotModalOpen) {
      closeElectricalTripleSpotModal();
    }
  }

  function togglePageExclusion(pageId) {
    if (isInteractionLocked) return;
    setExcludedPageIds((prev) =>
      prev.includes(pageId)
        ? prev.filter((id) => id !== pageId)
        : [...prev, pageId],
    );
  }

  async function handleSaveElectricalTripleSpot(saveOptions = {}) {
    if (
      isElectricalTripleSpotSaving ||
      !electricalTripleSpotPage?.file ||
      !electricalTripleSpotCircles.every((circle) => Boolean(circle))
    ) {
      return;
    }

    setIsElectricalTripleSpotSaving(true);
    const activeFile = electricalTripleSpotPage.file;
    const resolvedLocale = i18n.resolvedLanguage ?? i18n.language ?? "ko-KR";
    const requestedCreateInspectionResult =
      saveOptions &&
      typeof saveOptions === "object" &&
      saveOptions.createInspectionResult === true;

    try {
      const response = await postUpdateThermalInfo({
        refs: [electricalThermalCaptureTarget, electricalTripleSpotCaptureTarget],
        project_id: projectId ?? activeFile?.project_id,
        upload_id: activeFile?.upload_id,
        click_points: electricalTripleSpotCircles,
        create_inspection_result:
          electricalTripleSpotCircles.filter(Boolean).length >= 3 &&
          requestedCreateInspectionResult,
        currentPaletteOptionNumber: activeFile?.thermal_palette ?? 0,
        volume: activeFile?.volume,
        avg_decibel: activeFile?.avg_decibel,
        inspection_result: activeFile?.inspection_result,
        fusion_result: activeFile?.fusion_result_code ?? activeFile?.fusion_result,
        locale: resolvedLocale,
      });

      showAlert?.({
        text: response?.message,
        messageKey: response?.messageKey,
        messageParams: response?.messageParams,
        success: response?.success,
        autoClose: true,
      });

      if (!response?.success) {
        return;
      }

      const [, tripleSpotDataUrl] = await imgToBase64([
        electricalThermalCaptureTarget,
        electricalTripleSpotCaptureTarget,
      ]);

      setElectricalSafetyReportFiles((prev) =>
        prev.map((item) =>
          item?.upload_id === activeFile?.upload_id
            ? {
                ...item,
                click_points: [...electricalTripleSpotCircles],
                markup_image_triplespot:
                  tripleSpotDataUrl || item?.markup_image_triplespot || "",
              }
            : item,
        ),
      );

      const { sortKey, sortDir } = getFileSortPref(userId);
      const result = await getProjectElectricalSafetyReportData(projectId, {
        listScope,
        sortKey,
        sortDir,
      });
      const nextFiles = Array.isArray(result?.files) ? result.files : [];

      setElectricalSafetyReportFiles(nextFiles);

      setIsElectricalTripleSpotModalOpen(false);
      setElectricalTripleSpotPageId(null);
    } catch (error) {
      console.error(
        "[ProjectReportEditorModal] save electrical triple spot failed",
        error,
      );
      showAlert?.({
        textKey: "api.common.networkError",
        success: false,
        autoClose: true,
      });
    } finally {
      setIsElectricalTripleSpotSaving(false);
    }
  }

  function setListItemRef(pageId, node) {
    if (node) {
      itemRefs.current.set(pageId, node);
      return;
    }

    itemRefs.current.delete(pageId);
  }

  function movePageToIndex(pageId, nextIndex) {
    if (isInteractionLocked) return;
    if (!pageId) return;

    setOrderedPageIds((prev) => {
      const currentIndex = prev.indexOf(pageId);
      if (currentIndex < 0 || prev.length < 2) return prev;

      const boundedIndex = Math.min(Math.max(nextIndex, 0), prev.length - 1);
      if (boundedIndex === currentIndex) return prev;

      return reorderItems(prev, currentIndex, boundedIndex);
    });

    setSelectedPageId(pageId);
  }

  function handleMovePageByOffset(pageId, offset, event) {
    if (isInteractionLocked) return;
    event.stopPropagation();

    const currentIndex = orderedPages.findIndex((page) => page.id === pageId);
    if (currentIndex < 0) return;

    movePageToIndex(pageId, currentIndex + offset);
  }

  function handleListItemClick(pageId) {
    if (isInteractionLocked) return;
    selectProjectReportPage(pageId);
  }

  function handleListItemPositionChange(pageId, event) {
    if (isInteractionLocked) return;
    event.stopPropagation();

    const nextIndex = Number(event.target.value);
    if (!Number.isFinite(nextIndex)) return;

    movePageToIndex(pageId, nextIndex);
  }

  function renderListItemContent(page, index) {
    const isExcluded = excludedPageIds.includes(page.id);
    const canMoveUp = index > 0;
    const canMoveDown = index < orderedPages.length - 1;

    return (
      <>
        <div className="projectReportEditorListItemTopBar">
          <div className="projectReportEditorListItemTopBarActions">
            <div className="projectReportEditorListItemReorderControls">
              <button
                type="button"
                className="projectReportEditorListItemMoveButton"
                onClick={(event) => handleMovePageByOffset(page.id, -1, event)}
                disabled={isInteractionLocked || !canMoveUp}
                aria-label={t("projectDetail.projectReportEditor.moveUp", {
                  defaultValue: "Move up",
                })}
              >
                <ChevronUp size={14} strokeWidth={2.4} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="projectReportEditorListItemMoveButton"
                onClick={(event) => handleMovePageByOffset(page.id, 1, event)}
                disabled={isInteractionLocked || !canMoveDown}
                aria-label={t("projectDetail.projectReportEditor.moveDown", {
                  defaultValue: "Move down",
                })}
              >
                <ChevronDown size={14} strokeWidth={2.4} aria-hidden="true" />
              </button>
              <label
                className="projectReportEditorListItemPositionField"
                onClick={(event) => event.stopPropagation()}
              >
                <span>
                    No.        
                </span>
                <select
                  value={index}
                  disabled={isInteractionLocked}
                  onChange={(event) => handleListItemPositionChange(page.id, event)}
                >
                  {orderedPages.map((_, positionIndex) => (
                    <option key={`${page.id}-position-${positionIndex}`} value={positionIndex}>
                      {positionIndex + 1}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <button
              type="button"
              className={`projectReportEditorListItemExcludeButton ${
                isExcluded ? "is-restore" : "is-remove"
              }`}
              onClick={(event) => {
                event.stopPropagation();
                togglePageExclusion(page.id);
              }}
              disabled={isInteractionLocked}
              aria-label={
                isExcluded
                  ? t("projectDetail.projectReportEditor.includeInReport", {
                      defaultValue: "Include again",
                    })
                  : t("projectDetail.projectReportEditor.excludeFromReport", {
                      defaultValue: "Exclude from report",
                    })
              }
              title={
                isExcluded
                  ? t("projectDetail.projectReportEditor.includeInReport", {
                      defaultValue: "Include again",
                    })
                  : t("projectDetail.projectReportEditor.excludeFromReport", {
                      defaultValue: "Exclude from report",
                    })
              }
            >
              {isExcluded
                ? <RotateCcw size={13} strokeWidth={2.1} aria-hidden="true" />
                : <Trash2 size={13} strokeWidth={2.1} aria-hidden="true" />}
            </button>
          </div>
        </div>
        <div className="projectReportEditorListItemPreview">
          <ProjectReportListPreviewCard
            page={page}
            order={index + 1}
            selectedReportCategory={selectedReportCategory}
          />
        </div>
      </>
    );
  }

  return (
    <div
      className={embedded ? "projectReportWorkspace" : "projectReportEditorOverlay"}
      role="presentation"
      onClick={embedded ? undefined : handleRequestClose}
    >
      <div
        className={`projectReportEditorModal ${isInteractionLocked ? "is-busy" : ""}`}
        role={embedded ? "region" : "dialog"}
        aria-modal={embedded ? undefined : "true"}
        aria-label={t("projectDetail.projectReportEditor.title", {
          defaultValue: "Project Report Builder",
        })}
        onClick={(event) => event.stopPropagation()}
      >
        {isInteractionLocked ? (
          <div
            ref={busyShieldRef}
            className="projectReportEditorBusyShield"
            role="status"
            aria-live="polite"
            tabIndex={0}
            onKeyDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            <div className="projectReportEditorBusyCard">
              <div className="projectReportEditorBusyTitle">{exportProgressText}</div>
              <div className="projectReportEditorBusyNote">
                {t("projectDetail.projectReportEditor.busyNotice", {
                  defaultValue:
                    "Report generation is in progress. Please wait until the download is ready.",
                })}
              </div>
              <div className="projectReportEditorBusyPercent">{exportProgressPercent}%</div>
            </div>
          </div>
        ) : null}
        <div className="projectReportEditorSelectionBar">
          <div className="projectReportEditorSelectionGuideRow">
            <div className="projectReportEditorSelectionGuide">
              {selectionDescriptionText}
            </div>
            {!embedded && <div
                className={`projectReportEditorAction is-muted ${
                  isInteractionLocked ? "is-disabled" : ""
                }`}
                role="button"
                tabIndex={isInteractionLocked ? -1 : 0}
                onClick={handleRequestClose}
                onKeyDown={(event) => activateOnKeyDown(event, handleRequestClose)}
              >
                {t("editorBtnBox.cancelButton", { defaultValue: "Cancel" })}
              </div>}
          </div>
          <div className="projectReportEditorSelectionPrompt" role="status">{currentStep === 3 ? "3번에서 표지 포함 여부를 선택하면 내보낼 수 있습니다." : currentStep === 4 ? "선택이 완료되었습니다. 미리보기를 확인하고 파일을 내보내세요." : selectionGuideText}</div>
          <div className="projectReportEditorSelectionFlow">
            <div className={`projectReportEditorSelectionStep ${stepClass(1)}`}>
            <div className="projectReportEditorSelectionStepTitle">
              <span>1</span>
              {t("projectDetail.projectReportEditor.step.reportType", {
                defaultValue: "종류",
              })}
              {stepStatus(1)}
            </div>
            <div className="projectReportEditorSelectionOptions" role="radiogroup">
              {availableReportCategoryOptions.map((option) => (
                <div
                  key={option.id}
                  className={`projectReportEditorSelectionOption is-report ${
                    selectedReportCategory === option.id ? "is-active" : ""
                  }`}
                  role="radio"
                  aria-checked={selectedReportCategory === option.id}
                  tabIndex={isInteractionLocked ? -1 : 0}
                  onClick={() => {
                    if (isInteractionLocked) return;
                    setSelectedReportCategory(option.id);
                    setSelectedExportType("");
                  }}
                  onKeyDown={(event) =>
                    activateOnKeyDown(event, () => {
                      if (isInteractionLocked) return;
                      setSelectedReportCategory(option.id);
                      setSelectedExportType("");
                    })
                  }
                >
                  <div className="projectReportEditorSelectionRadio" />
                  <div className="projectReportEditorSelectionText">
                    {getReportCategoryLabel(option.id, t)}
                  </div>
                </div>
              ))}
            </div>
            {scopeConfirmed && (
              <div className="projectReportEditorOptionalConditions">
                <label className="projectReportEditorOptionalToggle">
                  <input type="checkbox" checked={useDetailConditions} disabled={isInteractionLocked || reportDataLoading}
                    onChange={event => {
                      setUseDetailConditions(event.target.checked);
                      if (event.target.checked) invalidateReportConditions();
                      else handleUd1000ReportFilterChange(getUd1000ReportAllFilterSelection());
                    }} />
                  <strong>상세 조건 사용</strong><small>선택 사항</small>
                </label>
                {!useDetailConditions && <p className="projectReportEditorStepFeedback">프로젝트 전체가 출력 대상입니다. 특정 경로나 조건으로 좁힐 때만 사용하세요.</p>}
                {useDetailConditions && (
                <ProjectReportDetailConditions
                  key={scopeKey}
                  projectId={projectId}
                  groups={selectedReportCategory === "ud1000_fusion" ? ud1000ReportFilterGroups : []}
                  appliedScope={listScope}
                  value={normalizedUd1000ReportFilterSelection}
                  onApply={handleUd1000ReportFilterChange}
                  onDraftChange={invalidateReportConditions}
                  disabled={isInteractionLocked || reportDataLoading}
                  t={t}
                />
                )}
              </div>
            )}
            {scopeConfirmed && (reportDataLoading ? <p className="projectReportEditorStepFeedback" role="status">대상 파일을 불러오는 중…</p>
              : reportDataError ? <div className="projectReportEditorStepActions"><p className="projectReportEditorStepFeedback is-error" role="alert">{reportDataError}</p><button type="button" className="projectReportEditorAction" disabled={isInteractionLocked} onClick={() => { setReportDataError(""); setLoadRequest({ scopeKey, listScope }); }}>다시 불러오기</button></div>
              : conditionsConfirmed && loadedRequest === loadRequest ? <p className="projectReportEditorStepFeedback" role="status">{activeFiles.length ? `${activeFiles.length}개 파일을 불러왔습니다. 2번에서 출력 형식을 선택하세요.` : "조건에 맞는 파일이 없습니다. 상세 조건에서 경로나 조건을 조정해 주세요."}</p> : null)}
            {pendingElectricalPages.length ? (
              <div className="projectReportEditorPendingNote">
                {t("projectDetail.projectReportEditor.pendingElectricalNotice", {
                  count: pendingElectricalPages.length,
                  defaultValue:
                    "Triple-Spot Analysis Required. {{count}} item(s) must complete Triple-Spot Analysis before report download.",
                })}
              </div>
            ) : null}
            </div>
            <div className={`projectReportEditorSelectionStep ${stepClass(2)}`}>
            <div className="projectReportEditorSelectionStepTitle">
              <span>2</span>
              {t("projectDetail.projectReportEditor.step.exportType", {
                defaultValue: "출력 파일 형식",
              })}
              {stepStatus(2)}
            </div>
            <div
              className="projectReportEditorSelectionOptions is-typeOptions"
              role="radiogroup"
            >
              {availableExportTypeOptions.map((option) => {
                const isEnabled = hasReadyReportScope;

                return (
                  <div
                    key={option.id}
                    className={`projectReportEditorSelectionOption is-type ${
                      selectedExportType === option.id ? "is-active" : ""
                    } ${isEnabled ? "" : "is-disabled"}`}
                    role="radio"
                    aria-checked={selectedExportType === option.id}
                    aria-disabled={!isEnabled}
                    tabIndex={!isInteractionLocked && isEnabled ? 0 : -1}
                    onClick={() => {
                      if (isInteractionLocked) return;
                      if (!isEnabled) return;
                      setSelectedExportType(option.id);
                      setCoverConfirmed(false);
                    }}
                    onKeyDown={(event) =>
                      activateOnKeyDown(event, () => {
                        if (isInteractionLocked) return;
                        if (!isEnabled) return;
                        setSelectedExportType(option.id);
                        setCoverConfirmed(false);
                      })
                    }
                  >
                    <div className="projectReportEditorSelectionRadio" />
                    <div className="projectReportEditorSelectionText">
                      {getExportTypeLabel(option.id, t)}
                    </div>
                  </div>
                );
              })}
            </div>
            {!hasReadyReportScope && <p className="projectReportEditorStepFeedback">대상 파일을 불러온 후 선택할 수 있습니다.</p>}
            </div>
              <div className={`projectReportEditorSelectionStep is-coverStep ${stepClass(3)}`}>
              <div className="projectReportEditorSelectionStepTitle">
                <span>3</span>
                {t("projectDetail.projectReportEditor.coverOptionLabel", {
                  defaultValue: "Cover page",
                })}
                {hasReadyReportScope && selectedExportType && !needsCoverChoice ? <small>해당 없음</small> : stepStatus(3)}
              </div>
              <div
                className="projectReportEditorSelectionOptions is-coverOptions"
                role="radiogroup"
              >
                {COVER_PAGE_OPTIONS.map((option) => (
                  <div
                    key={option.id}
                    className={`projectReportEditorSelectionOption is-type ${
                      coverConfirmed && includeCoverPage === option.value ? "is-active" : ""
                    } ${canSelectCover ? "" : "is-disabled"}`}
                    role="radio"
                    aria-checked={coverConfirmed && includeCoverPage === option.value}
                    aria-disabled={!canSelectCover}
                    tabIndex={canSelectCover ? 0 : -1}
                    onClick={() => {
                      if (!canSelectCover) return;
                      setIncludeCoverPage(option.value);
                      setCoverConfirmed(true);
                    }}
                    onKeyDown={(event) =>
                      activateOnKeyDown(event, () => {
                        if (!canSelectCover) return;
                        setIncludeCoverPage(option.value);
                        setCoverConfirmed(true);
                      })
                    }
                  >
                    <div className="projectReportEditorSelectionRadio" />
                    <div className="projectReportEditorSelectionText">
                      {t(`projectDetail.projectReportEditor.coverOption.${option.id}`, {
                        defaultValue: option.label,
                      })}
                    </div>
                  </div>
                ))}
              </div>
              {(!hasReadyReportScope || !selectedExportType) && <p className="projectReportEditorStepFeedback">1번 대상 파일과 2번 출력 형식을 선택한 후 선택할 수 있습니다.</p>}
              {hasReadyReportScope && selectedExportType && !needsCoverChoice && <p className="projectReportEditorStepFeedback">선택한 보고서·출력 형식에는 표지 선택이 적용되지 않습니다.</p>}
              </div>
          </div>
        </div>

          {/* The report page already provides these fields in ProjectReportInfoForm. */}
          {!embedded && <div className="projectReportEditorProject">
            <div
              className={`projectReportEditorProjectSummary ${
                isProjectSummaryOpen ? "is-open" : ""
              }`}
              role="button"
              tabIndex={0}
              onClick={() => setIsProjectSummaryOpen((prev) => !prev)}
              onKeyDown={(event) =>
                activateOnKeyDown(event, () =>
                  setIsProjectSummaryOpen((prev) => !prev),
                )
              }
              >
              <div className="projectReportEditorProjectTitle">{projectSummary.title}</div>
              <div className="projectReportEditorProjectToggle">
                {isProjectSummaryOpen
                  ? t("projectDetail.projectReportEditor.projectToggle.hide", {
                      defaultValue: "Hide details",
                    })
                  : t("projectDetail.projectReportEditor.projectToggle.show", {
                      defaultValue: "Summary details",
                    })}
              </div>
            </div>
            {isProjectSummaryOpen ? (
              <div className="projectReportEditorProjectDetails">
                {projectSummary.lines.map((line, index) => (
                  <div className="projectReportEditorProjectLine" key={`summary-line-${index}`}>
                    {line}
                  </div>
                ))}
              </div>
            ) : null}
          </div>}
        
        {shouldShowReportFileGuide ? (
          <div className="projectReportEditorFileGuide" role="status" aria-live="polite">
            <div className="projectReportEditorFileGuideTitle">
              {reportFileGuideTitle}
            </div>
            <div className="projectReportEditorFileGuideText">
              {reportFileGuideDescription}
            </div>
          </div>
        ) : null}

        {shouldRenderReportMain ? (
          <div className="projectReportEditorMain">
          <div className="projectReportEditorCurrent">
            <div className="projectReportEditorCurrentLabel">
              <span className="projectReportEditorCurrentTitle">{loading
                || reportDataLoading
                ? t("projectDetail.projectReportEditor.loadingFiles", {
                    defaultValue: "Loading project files...",
                  })
                  : selectedPage?.title}</span>
          <div className="projectReportEditorActions">
            <div
              className={`projectReportEditorAction ${canExport ? "" : "is-disabled"}`}
              role="button"
              aria-disabled={!canExport || isInteractionLocked}
              tabIndex={!isInteractionLocked && canExport ? 0 : -1}
              onClick={handleExport}
              onKeyDown={(event) => activateOnKeyDown(event, handleExport)}
            >
              {exportActionLabel}
            </div>
          </div>
            </div>
            {shouldRenderCurrentFrame ? (
              <div className="projectReportEditorCurrentFrame" ref={currentFrameRef}>
                {loading || reportDataLoading ? (
                  <div className="projectReportEditorLoadingState">
                    {t("projectDetail.projectReportEditor.loadingFiles", {
                      defaultValue: "Loading project files...",
                    })}
                  </div>
                ) : (
                  <>
                    <ProjectReportPreviewPage
                      page={selectedPage}
                      t={t}
                      selectedReportCategory={selectedReportCategory}
                      i18n={i18n}
                      currentUser={currentUser}
                      onRequireElectricalTripleSpot={openElectricalTripleSpotModal}
                      previewMaxHeight={currentPreviewFrameHeight}
                    />
                    {selectedReportCategory === "electrical_duty" &&
                    isElectricalTripleSpotModalOpen &&
                    electricalTripleSpotPage?.file ? (
                      <ElectricalSafetyTripleSpotModal
                        embedded
                        file={electricalTripleSpotPage.file}
                        circles={electricalTripleSpotCircles}
                        setCircles={setElectricalTripleSpotCircles}
                        thermalCaptureTarget={electricalThermalCaptureTarget}
                        tripleSpotCaptureTarget={electricalTripleSpotCaptureTarget}
                        currentPaletteOptionNumber={
                          electricalTripleSpotPage.file?.thermal_palette ?? 0
                        }
                        isSaving={isElectricalTripleSpotSaving}
                        onClose={closeElectricalTripleSpotModal}
                        onSave={handleSaveElectricalTripleSpot}
                      />
                    ) : null}
                  </>
                )}
              </div>
            ) : null}
          </div>

          <div className="projectReportEditorList">
          {selectedReportCategory && (
          <div className="projectReportEditorStats">
            <div
              className="projectReportEditorStatChart"
              style={buildProjectReportChartStyle(projectSummary.chart)}
            >
              <div className="projectReportEditorStatChartCenter">
                <div className="projectReportEditorStatChartValue">
                  {projectSummary.chart.centerValue}
                </div>
                <div className="projectReportEditorStatChartLabel">
                  {projectSummary.chart.centerLabel}
                </div>
              </div>
            </div>
            <div className="projectReportEditorStatMeta">
              <div className="projectReportEditorStatSummary">
                <div className="projectReportEditorStatSummaryLine">
                  <span className="projectReportEditorStatSummaryValue">
                    {t("projectDetail.projectReportEditor.totalGeneratedPages", {
                      count: totalGeneratedPageCount,
                      defaultValue: `Total ${totalGeneratedPageCount} pages`,
                    })}
                  </span>
                  {coverPageCount > 0 ? (
                    <span className="projectReportEditorStatSummaryBadge">
                      {t("projectDetail.projectReportEditor.coverIncluded", {
                        defaultValue: "Including cover",
                      })}
                    </span>
                  ) : null}
                </div>
                <div className="projectReportEditorStatSummaryLine is-muted">
                  {t("projectDetail.projectReportEditor.excludedExportItems", {
                    count: excludedExportItemCount,
                    defaultValue: `Excluded ${excludedExportItemCount} items`,
                  })}
                </div>
              </div>
              {projectSummary.legend.map((item) => (
                <div className="projectReportEditorStat" key={item.label}>
                  <div
                    className={`projectReportEditorStatTone ${item.toneClassName}`}
                  />
                  <div className="projectReportEditorStatText">
                    {item.label} {item.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}
            {orderedPages.map((page, index) => (
              <div
                key={page.id}
                className={`projectReportEditorListItem ${
                  selectedPageId === page.id ? "is-active" : ""
                } ${excludedPageIds.includes(page.id) ? "is-excluded" : ""} ${
                  excludedPageIds.includes(page.id) ? "is-exportExcluded" : ""
                } ${page.requiresElectricalAnalysis ? "is-analysisRequired" : ""} ${
                  selectedReportCategory === "electrical_duty" &&
                  !page.requiresElectricalAnalysis
                    ? "is-analysisComplete"
                    : ""
                }`}
                role="button"
                tabIndex={isInteractionLocked ? -1 : 0}
                ref={(node) => setListItemRef(page.id, node)}
                onClick={() => handleListItemClick(page.id)}
                onKeyDown={(event) =>
                  activateOnKeyDown(event, () => handleListItemClick(page.id))
                }
              >
                {renderListItemContent(page, index)}
              </div>
            ))}
          </div>
        </div>
        ) : null}

        <div className="projectReportEditorExportStage" aria-hidden="true">
          <div className="projectReportEditorExportInner" ref={exportRef}>
            {usePdfTemplateCover ? (
              pdfTemplateCoverPages.map((page) => (
                <ProjectReportPreviewPage
                  key={page.id}
                  page={page}
                  t={t}
                  forExport
                  selectedReportCategory={selectedReportCategory}
                  i18n={i18n}
                  currentUser={currentUser}
                />
              ))
            ) : showCoverOption && includeCoverPage ? (
              <ProjectReportCoverPage
                projectSummary={projectSummary}
                reportLabel={selectedReportLabel}
                t={t}
              />
            ) : null}
            {exportableOrderedPages.map((page) => (
                <ProjectReportPreviewPage
                  key={`export-${page.id}`}
                  page={page}
                  t={t}
                  forExport
                  selectedReportCategory={selectedReportCategory}
                  i18n={i18n}
                  currentUser={currentUser}
                />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
